import { CRM_PIPELINE_API } from 'modules/crm/api/crm-pipeline-api'
import { ModalCreatePipeline } from 'modules/crm/components/create-pipeline'
import { CrmPipelineLoco } from 'modules/crm/constants/crm-pipeline-loco'
import { useCrmPipelineList } from 'modules/crm/hooks/use-crm-pipeline-list'
import { useDeletePipeline } from 'modules/crm/hooks/use-delete-pipline-'
import { ModalEditPipeline } from 'modules/crm/pipeline/components/edit-pipeline'
import { PipelineInterface } from 'modules/crm/types/pipeline-interface'
import { getPipelineUrl } from 'modules/crm/utils/get-pipeline-url'
import { NextPage } from 'next'
import React from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const CrmPipelines: NextPage = () => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const { limit, setLimit, pagination, setPagination } = usePagination()
  const { crmPipelineList, mutate, counter } = useCrmPipelineList({ pagination, limit })
  const { deletePipeline } = useDeletePipeline()

  const updateData = useUpdateData({
    setPagination,
    api: CRM_PIPELINE_API,
  })

  const onUpdateData = async () => {
    await updateData(false, crmPipelineList)
  }

  const columns: ColumnDefinitionInterface<PipelineInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 70,
      onRender: pipeline => (
        <LinkWithoutPrefetch
          rel="noreferrer"
          className="primary-link w-full"
          href={getPipelineUrl(pipeline.id)}
        >
          {pipeline.name}
        </LinkWithoutPrefetch>
      ),
    },
    {
      key: 'pipeline.createdAt',
      header: t('global.created_on'),
      widthPercent: 30,
      onRender: pipeline => (
        <span>
          {dateTimeFormat(pipeline.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const onRemovePipeline = async (pipelineId: number) => {
    await deletePipeline(pipelineId)
    await onUpdateData()
  }

  const mutateData = async (response: PipelineInterface) => {
    await updateData(false, undefined, {
      mutationData: crmPipelineList,
      onMutate: async () => {
        await mutate(data => {
          if (data) {
            return {
              items: data.items.map(item => {
                if (item.id === response.id) {
                  return response
                }
                return item
              }),
              hasMore: data.hasMore,
            }
          }
        }, false)
      },
    })
  }

  return (
    <ListingLayout
      counter={counter}
      data={crmPipelineList}
      title={t('dashboard.crm_pipeline.title_plural')}
      type={'compact'}
      customHeaderElement={<ModalCreatePipeline onUpdateData={onUpdateData} />}
    >
      <Table
        data={crmPipelineList}
        columns={columns}
        caption={t('dashboard.crm_pipeline.title')}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actionsOrder={['remove']}
        actions={{
          custom: [
            {
              name: 'edit',
              caption: t('global.edit'),
              onActionRender: (opened, onClose, pipeline, key) => (
                <ModalEditPipeline
                  key={key}
                  pipeline={pipeline}
                  mutate={mutateData}
                  opened={opened}
                  onClose={onClose}
                />
              ),
            },
          ],
          remove: {
            onChange: pipeline => onRemovePipeline(pipeline.id),
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(CrmPipelineLoco)

export default CrmPipelines
